import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { TextInputWithoutTopMargin } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/CapManageSidebar.styled.tsx';
import { DeleteEntityDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/DeleteEntityDrawer/DeleteEntityDrawer';
import { PersonCard } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/PersonsList/PersonCard.tsx';
import { PersonsList } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/PersonsList/PersonsList.tsx';
import { TAGS_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/utils.ts';
import { OverflowBadgeList } from '@dealstack/frontend/app/pages/Valuations/components/OverflowBadgeList/OverflowBadgeList.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue.ts';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { LegalPersonTags, PermissionRole, PersonType } from '@dealstack/shared/lib/generated';
import { EMPTY_CALLBACK } from '@dealstack/shared/lib/utils.ts';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex } from '@mantine/core';
import { useContext, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { BadgeListItem } from '@dealstack/frontend/app/pages/Valuations/components/OverflowBadgeList/OverflowBadgeList.utils.types';
import type { PersonSearchForm } from './CapManageSidebar.types';

export const CapManageSidebar = () => {
	const tCapManage = useTranslationCapManage();

	const personSearchForm = useForm<PersonSearchForm>({
		defaultValues: { search: '' },
	});
	const personSearchValue = useWatch({
		control: personSearchForm.control,
		name: 'search',
	});

	const containerRef = useRef<HTMLDivElement | null>(null);

	const {
		selectedEntityId,
		setSelectedEntityId,
		dealId,
		capForceDeleteFlag,
		refetchPersonsAndTags,
		persons,
		personsTags,
		createEntityMode,
		setCreateEntityMode,
		newEntityComputedName,
	} = useContext(CapManageControllerContext);

	const [isManageMode, setIsManageMode] = useState<boolean>(false);
	const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set());

	const [isConfirmDeleteOpened, setIsConfirmDeleteOpened] = useState<boolean>(false);
	const [isForceDelete, setIsForceDelete] = useState(false);
	const [selectedPersonTags, setSelectedPersonTags] = useState<LegalPersonTags[]>([]);

	const debouncedSearchValue = useDebounceValue(personSearchValue, 300);
	const selectedEntitiesCountText = useMemo(
		() => (selectedEntities.size ? `(${selectedEntities.size})` : ''),
		[selectedEntities.size]
	);

	const searchFilteredPersons = useMemo(() => {
		if (!persons?.length) {
			return [];
		}

		return persons.filter((person) => {
			const searchString = debouncedSearchValue.toLowerCase().trim();
			let nameMatch = true;
			let tagMatch = true;

			if (searchString) {
				const lowerCaseName = person.computedName.toLowerCase();
				nameMatch = lowerCaseName.includes(searchString);
			}

			if (selectedPersonTags.length) {
				tagMatch = selectedPersonTags.some((tag) => person.tags.includes(tag));
			}

			return nameMatch && tagMatch;
		});
	}, [persons, debouncedSearchValue, selectedPersonTags]);

	const mappedPersonTagBadges = useMemo<BadgeListItem[]>(() => {
		if (!personsTags?.length) {
			return [];
		}

		const handleSelectedPersonTags = (tag: LegalPersonTags | 'ALL_TAG') => {
			if (tag === 'ALL_TAG') {
				setSelectedPersonTags([]);
				return;
			}

			if (selectedPersonTags.includes(tag)) {
				setSelectedPersonTags(selectedPersonTags.filter((t) => t !== tag));
			} else {
				setSelectedPersonTags([...selectedPersonTags, tag]);
			}
		};

		const allTag: BadgeListItem = {
			id: 'ALL_TAG',
			label: `${tCapManage('sidebar.tags.all')} (${persons?.length})`,
			onBadgeClick: () => handleSelectedPersonTags('ALL_TAG'),
			selected: !selectedPersonTags.length,
			testId: makeComplexDataTestId(
				TEST_ELEMENT_IDS.CAP_SIDEBAR_CLASSIFIER_FILTER_TAB,
				'ALL_TAG'
			),
		};
		const tagBadges = personsTags.map((tag): BadgeListItem => {
			const matchingTag = TAGS_OPTIONS.find((option) => option.value === tag.name);
			const tagEnumValue = tag.name;
			const selected = selectedPersonTags.includes(tagEnumValue);

			return {
				id: tagEnumValue,
				label: `${tCapManage(matchingTag?.label || '')} (${tag.count})`,
				onBadgeClick: () => handleSelectedPersonTags(tagEnumValue),
				selected,
				testId: makeComplexDataTestId(
					TEST_ELEMENT_IDS.CAP_SIDEBAR_CLASSIFIER_FILTER_TAB,
					tagEnumValue
				),
			};
		});

		return [allTag, ...tagBadges];
	}, [personsTags, persons, tCapManage, selectedPersonTags]);

	const handleClearPersonSearch = () => {
		personSearchForm.setValue('search', '');
	};

	const cancelManageMode = () => {
		setIsManageMode(false);
		setSelectedEntities(new Set());
	};

	return (
		<Flex
			direction="column"
			gap="1rem"
			h="100%"
			w="100%"
			data-testid={TEST_ELEMENT_IDS.CAP_SIDEBAR}
			ref={containerRef}
		>
			<Flex
				direction="column"
				h="100%"
				gap="1rem"
				miw={containerRef.current?.offsetWidth}
				style={{ position: 'relative', overflow: 'hidden' }}
			>
				{!!persons?.length && (
					<Flex gap="0.5rem" align="flex-start">
						<FormProvider {...personSearchForm}>
							<TextInputWithoutTopMargin
								name="search"
								control={personSearchForm.control}
								placeholder={tCapManage('sidebar.search.placeholder')}
								size="md"
								leftSection={<Icon variant="search-v3" size="lg" color="black" />}
								data-testid={TEST_ELEMENT_IDS.CAP_SIDEBAR_SEARCH_INPUT}
								rightSection={
									<Icon
										variant="close-v3"
										onClick={handleClearPersonSearch}
										size="md"
										color="black"
										style={{ cursor: 'pointer' }}
									/>
								}
							/>
						</FormProvider>
						<PermissionWrapper
							resourceType={ResourceType.DEAL}
							resourceId={defaultToEmptyString(dealId)}
							permissionRole={PermissionRole.EDITOR}
						>
							{isManageMode ? (
								<Button variant="subtle" h="2.5rem" onClick={cancelManageMode}>
									{tCapManage('sidebar.manageCancel')}
								</Button>
							) : (
								<Button
									disabled={!searchFilteredPersons?.length}
									variant="subtle"
									h="2.5rem"
									onClick={() => setIsManageMode(true)}
								>
									{tCapManage('sidebar.manage')}
								</Button>
							)}
						</PermissionWrapper>
					</Flex>
				)}

				{!!mappedPersonTagBadges?.length && !isManageMode && (
					<OverflowBadgeList badges={mappedPersonTagBadges} />
				)}

				{isManageMode ? (
					<PermissionWrapper
						resourceType={ResourceType.DEAL}
						resourceId={defaultToEmptyString(dealId)}
						permissionRole={PermissionRole.EDITOR}
					>
						<Flex align="center" gap="0.5rem">
							<Button
								variant="subtle"
								color="red"
								onClick={() => {
									setIsForceDelete(false);
									setIsConfirmDeleteOpened(true);
								}}
								disabled={!selectedEntities.size}
							>
								{tCapManage('sidebar.delete')}
								{selectedEntitiesCountText}
							</Button>

							{capForceDeleteFlag && (
								<Button
									variant="subtle"
									color="red"
									onClick={() => {
										setIsForceDelete(true);
										setIsConfirmDeleteOpened(true);
									}}
									disabled={!selectedEntities.size}
								>
									Force {selectedEntitiesCountText}
								</Button>
							)}
						</Flex>
					</PermissionWrapper>
				) : null}

				{!!createEntityMode && (
					<PersonCard
						isSelected={true}
						person={{
							id: 'new_entity',
							computedName: newEntityComputedName || 'Name',
							type: createEntityMode,
							hasSecuritiesWithInvalidTransactions: false,
							tags: [],
						}}
						showCheckbox={false}
						hidePin={true}
						personCheckboxValue={false}
						isPinned={false}
						onCardClick={EMPTY_CALLBACK}
						onPinValueChange={EMPTY_CALLBACK}
						onPersonCheckboxChange={EMPTY_CALLBACK}
					/>
				)}

				<PersonsList
					dealId={dealId}
					persons={searchFilteredPersons}
					selectedPersonId={selectedEntityId}
					setSelectedPersonId={(id) => {
						setSelectedEntityId(id);

						if (createEntityMode) {
							setCreateEntityMode(null);
						}
					}}
					selectedPersons={selectedEntities}
					setSelectedPersons={setSelectedEntities}
					isManageMode={isManageMode}
					isCreateMode={!!createEntityMode}
					onAddPersonClick={() => {
						setSelectedEntityId(null);
						setCreateEntityMode(PersonType.LEGAL);
					}}
				/>

				<DeleteEntityDrawer
					opened={isConfirmDeleteOpened}
					onClose={() => setIsConfirmDeleteOpened(false)}
					resetSelectedEntities={() => setSelectedEntities(new Set())}
					refreshEntitiesLists={refetchPersonsAndTags}
					selectedEntities={selectedEntities}
					forceDelete={isForceDelete}
				/>
			</Flex>
		</Flex>
	);
};
